import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";

import SearchField from "../../components/searchField";
import CommonSelect, { MenuOption } from "../../components/commonSelect";
import ValidatedInfoChip from "./validatedInfo";
import { useAppDispatch } from "../../helpers/hooks";

import {
  fetchAllEventsList,
  AllEventsResponse,
  Event as AllEventsApiEvent,
} from "../../redux/slices/allEventslistSlice";
import { fetchEventSlots } from "../../redux/slices/eventSlotsSlice";
import {
  Volunteer,
  VolunteerValidation,
} from "../../redux/slices/validateVolunteer";
import { registerForEvent } from "../eventDetails/eventRegistrationSlice";
import { EventSlotState } from "../../pages/eventDetails";
import { convertDate, convertTimeTo12h } from "../../helpers/constant";
export interface EventOption {
  eventId: string;
  eventName: string;
  eventInfo: string;
}

export interface SlotOption {
  [slotId: string]: string;
}

const initialEventData: EventOption = {
  eventId: "",
  eventName: "",
  eventInfo: "",
};

interface Props {
  onValidateShortId?: (shortId: string) => Promise<string>;
  onValidateEvent?: (eventId: string) => Promise<string>;
  onRegister?: (payload: {
    shortId: string;
    eventId?: string;
    slotId?: string;
  }) => Promise<void>;
  onToast: (type: "success" | "error" | "info", message: string) => void;
}

const RegisterVolunteer: React.FC<Props> = ({
  onValidateShortId,
  onValidateEvent,
  onRegister,
  onToast,
}) => {
  const [shortId, setShortId] = useState("");
  const [selectedEventData, setSelectedEventData] = useState<EventOption>(initialEventData);
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [selectedEventName, setSelectedEventName] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [validatedUser, setValidatedUser] = useState<Volunteer | null>(null);
  const [validatedEventInfo, setValidatedEventInfo] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [allEvents, setAllEvents] = useState<EventOption[]>([]);
  const [eventAllSlots, setEventAllSlots] = useState<MenuOption[]>([]);
  const [slots, setSlots] = useState<EventSlotState[]>([]);
  const [slotMessage, setSlotMessage] = useState("");
  const [validateVolunteerMessage, setValidateVolunteerMessage] = useState("");
  const [selectedTransport, setSelectedTransport] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const dispatch = useAppDispatch();
  const sanitizeShortId = (value: string) =>
    value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const raw = (await dispatch(
          fetchAllEventsList("active"),
        ).unwrap()) as AllEventsResponse | AllEventsApiEvent[];

        const list: AllEventsApiEvent[] = Array.isArray(raw)
          ? raw
          : (raw.active ?? []);

        const data: EventOption[] = list.map((event) => ({
          eventId: String(event.eventId),
          eventName: event.eventName,
          eventInfo: (event as any).eventInfo ?? "",
        }));

        setAllEvents(data);
      } catch (error) {
        console.error("Error fetching token", error);
      }
    };
    fetchEvents();
  }, []);

  const allEventOptions = useMemo(() => {
    return allEvents?.map((event) => ({
      label: event.eventName,
      value: String(event.eventId),
    }));
  }, [allEvents]);

  const allSlotsOptions = useMemo(() => {
      const allSlots = slots || [];
  
      const grouped = allSlots.reduce((acc: any, slot: EventSlotState) => {
        const date = convertDate(slot.eventSlotStartDate);
  
        if (!acc[date]) acc[date] = [];
  
        acc[date].push({
          label: `${convertTimeTo12h(slot.eventSlotStartTime)} to ${convertTimeTo12h(slot.eventSlotEndTime)}`,
          value: String(slot.slotId),
        });
  
        return acc;
      }, {});
      return Object.entries(grouped).map(([date, options]) => ({
        header: date,
        options: options as MenuOption[],
      }));
    }, [slots]);

  const transportOptions: MenuOption[] = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const locationOptions: MenuOption[] = [
    { label: "EC", value: "EC" },
    { label: "BTG", value: "BTG" },
    { label: "ETZ", value: "ETZ" },
  ];

  const handleSearch = async () => {
    if (!shortId) return;

    if (shortId.trim().length < 7) {
      onToast("error", "Please provide a valid Short ID");
      return;
    }

    try {
      setLoading(true);
      const response = await dispatch(VolunteerValidation(shortId));

      if (typeof response === "string") {
        // No valid user
        setValidateVolunteerMessage(response);
        setValidatedUser(null);
      } else {
        // Valid user
        if (Array.isArray(response.payload) && response.payload.length > 0) {
          setValidatedUser(response.payload[0]);
          setValidateVolunteerMessage("");
        } else {
          setValidateVolunteerMessage("User Not Found");
          setValidatedUser(null); // no user found
        }
      }
    } catch (error) {
      console.error("Error fetching token", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = async (eventId: string) => {
    setSelectedEvent(eventId);
    const event = allEvents?.find((event) => +event.eventId === +eventId);
    setSelectedEventData(event ?? initialEventData);
    setSelectedEventName(event ? event.eventName : "");

    // Reset slot when event changes
    setSelectedSlot("");
    setSlots([]);

    try {
      const res = await dispatch(fetchEventSlots(eventId)).unwrap();
      if (Array.isArray(res) && res.length > 0) {
        setSlots(res);
        setSlotMessage("");
      } else {
        setSlots([]);
        setSlotMessage("Slots are not available");
      }
    } catch (err) {
      console.error(err);
      setSlotMessage("Slots are not available");
    }
  };

  const handleRegister = async () => {
    if (selectedEvent === "") {
      onToast("error", "Please select an event");
      return;
    }

    if (selectedSlot === "") {
      onToast("error", "Please select a slot");
      return;
    }

    const payload = {
      eventId: selectedEvent,
      eventName: selectedEventName,
      mailSent: false,
      waitlisted: false,
      deletedAt: "",
      userEmail: "",
      userId: validatedUser?.id,
      shortId: shortId,
      eventSlotId: selectedSlot,
      transport: selectedTransport.toLowerCase() === "yes" ? true : false,
      location: selectedLocation,
    };

    try {
      setLoading(true);
      const registerRes = await dispatch(registerForEvent(payload));

      if (registerRes.meta.requestStatus === "fulfilled") {
        setShortId("");
        clearSelections();
        onToast("success", "Volunteer registered successfully!");
      } else {
        const errorMessage: string =
          registerRes.payload?.response?.data || "Registration failed.";
        // showToast(errorMessage, "error");
        onToast("error", errorMessage);
      }
    } catch (error) {
      onToast("error", "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  function clearSelections() {
    setValidatedUser(null);
    setSelectedEvent("");
    setSelectedEventName("");
    setSelectedSlot("");
    setSelectedTransport("");
    setSelectedLocation("");
    setValidateVolunteerMessage("");
  }

  const handleShortIdChange = (value: string) => {
    clearSelections();
    setShortId(sanitizeShortId(value));
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        borderColor: "#000",
        paddingLeft: 4,
        paddingRight: 4,
      }}
    >
      <Grid container spacing={3} alignItems="flex-start">
        {/* Volunteer Search */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SearchField
            value={shortId.toUpperCase()}
            placeholder="Enter Volunteer Short ID"
            onChange={handleShortIdChange}
            onSearch={handleSearch}
          />

          {validatedUser && (
            <Box mt={2}>
              <ValidatedInfoChip label={validatedUser.userName} valid={true} />
            </Box>
          )}
          {validateVolunteerMessage && (
            <Box mt={2}>
              <ValidatedInfoChip
                label={validateVolunteerMessage}
                valid={false}
              />
            </Box>
          )}
        </Grid>

        {/* Select Event and Select Slot */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Grid container spacing={3}>
            {/* Select Event */}
            <Grid size={{ xs: 12, md: 4 }}>
              <CommonSelect
                searchable
                placeholder="Select Event"
                value={selectedEvent}
                options={allEventOptions}
                showSectionHeader={false}
                onChangeHandler={handleEventChange}
                disabled={validatedUser ? false : true}
              />
            </Grid>

            {/* Select Slot */}
            <Grid size={{ xs: 12, md: 4 }}>
              <CommonSelect
                placeholder={
                  slotMessage ? "Slots are not available" : "Select Time Slot"
                }
                value={selectedSlot}
                showSectionHeader
                options={[]}
                groupedOptions={allSlotsOptions}
                onChangeHandler={setSelectedSlot}
                disabled={validatedUser && selectedEvent ? false : true}
              />
            </Grid>

            {/* Transport */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {/* <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.8,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Do you require transport assistance?
            </Typography> */}
            <CommonSelect
              placeholder="Select Transport"
              value={selectedTransport}
              options={transportOptions}
              showSectionHeader={false}
              onChangeHandler={setSelectedTransport}
              disabled={!validatedUser}
              scrollContainerId="select-transport"
            />
          </Grid>
          </Grid>
          {validatedUser && selectedEvent && selectedSlot && (
            <ValidatedInfoChip
              label={`Event Info: ${selectedEventName}, ${selectedEventData.eventInfo}`} //validatedEventInfo
              valid={true}
            />
          )}
          {/* Event Info */}
          {validatedEventInfo && (
            <Box mt={2}>
              <ValidatedInfoChip label={validatedEventInfo} valid={true} />
            </Box>
          )}
        </Grid>
      </Grid>
      <Box
        sx={{
          borderRadius: 1,
          borderColor: "#000",
          paddingTop: 2,
          paddingBottom: 4,
        }}
      >
        <Grid container spacing={3}>
          

          {/* Location */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {/* <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.8,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Choose your location
            </Typography> */}
            <CommonSelect
              placeholder="Select Location"
              value={selectedLocation}
              options={locationOptions}
              showSectionHeader={false}
              onChangeHandler={setSelectedLocation}
              disabled={!validatedUser}
              scrollContainerId="select-location"
            />
          </Grid>
          {/* Button */}
            <Grid
              size={{ xs: 12, md: 1 }}
              sx={{minWidth: '120px'}}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <LoadingButton
                variant="contained"
                loading={loading}
                onClick={handleRegister}
                disabled={
                  !(
                    validatedUser &&
                    selectedEvent &&
                    selectedSlot &&
                    selectedTransport &&
                    selectedLocation
                  )
                }
                sx={{
                  height: 56,
                  px: 2,
                  // fontSize: "16px",
                  width: '120px',
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  "&.Mui-disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#757575",
                },
                }}
              >
                Register
              </LoadingButton>
            </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default RegisterVolunteer;
